// ════════════════════════════════════════════════════════════════════
// nutrition-recipes.ts — full recipes with ingredients (grams +
// household), steps, allergen flags. 28 recipes across 4 food-relevant
// age bands.
//
// Methodology references (NOT copied):
//   - Annabel Karmel — first-foods structure, texture progression
//   - Ellyn Satter — Division of Responsibility, no-pressure feeding
//   - Karp / Cadenhead — choking-safety conventions
//   - AAP / DGKJ / WHO — iron-first, allergen-early, no honey <12mo,
//     no whole milk as drink <12mo, salt minimization
//
// All recipes are originals written from first principles in this
// methodology — no recipe is reproduced from a copyrighted source.
//
// Localization:
//   - Each user-facing string has an _de twin
//   - localeRecipe(recipe) returns a flat view in the active locale
// ════════════════════════════════════════════════════════════════════

import { isGerman } from '../i18n/locale.js';

export type RecipeAllergen =
  | 'egg'
  | 'dairy'
  | 'wheat'
  | 'soy'
  | 'peanut'
  | 'tree_nut'
  | 'fish'
  | 'shellfish'
  | 'sesame';

export interface RecipeIngredient {
  name: string;
  name_de: string;
  metric: string; // metric stays the same in both locales
  household: string;
  household_de: string;
}

export interface RecipeStep {
  text: string;
  text_de: string;
}

export interface Recipe {
  id: string;
  bandId: string;
  dayOfWeek: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
  title: string;
  title_de: string;
  whyForThisAge: string;
  whyForThisAge_de: string;
  prepMinutes: number;
  cookMinutes: number;
  servings: string;
  servings_de: string;
  ingredients: RecipeIngredient[];
  steps: RecipeStep[];
  allergens: RecipeAllergen[];
  notes: string[];
  notes_de: string[];
}

// ────────────────────────────────────────────────────────────────────
// Allergen labels
// ────────────────────────────────────────────────────────────────────

export const ALLERGEN_LABEL: Record<RecipeAllergen, string> = {
  egg: 'Egg',
  dairy: 'Dairy',
  wheat: 'Wheat',
  soy: 'Soy',
  peanut: 'Peanut',
  tree_nut: 'Tree nuts',
  fish: 'Fish',
  shellfish: 'Shellfish',
  sesame: 'Sesame',
};

export const ALLERGEN_LABEL_DE: Record<RecipeAllergen, string> = {
  egg: 'Ei',
  dairy: 'Milchprodukte',
  wheat: 'Weizen/Gluten',
  soy: 'Soja',
  peanut: 'Erdnuss',
  tree_nut: 'Baumnüsse',
  fish: 'Fisch',
  shellfish: 'Schalentiere',
  sesame: 'Sesam',
};

export function localeAllergenLabel(a: RecipeAllergen): string {
  return (isGerman() ? ALLERGEN_LABEL_DE : ALLERGEN_LABEL)[a];
}

// ════════════════════════════════════════════════════════════════════
// RECIPES
// ════════════════════════════════════════════════════════════════════

export const RECIPES: Recipe[] = [

  // ╔══════════════════════════════════════════════════════════════╗
  // ║ Stage: 6–8 months · Beikoststart                            ║
  // ║ Iron-first purees + allergen introduction. Smooth → grob.   ║
  // ╚══════════════════════════════════════════════════════════════╝

  {
    id: 'sweet-potato-lentil-puree-6-8',
    bandId: 'starting-solids',
    dayOfWeek: 'mon',
    title: 'Sweet potato & red lentil puree',
    title_de: 'Süsskartoffel-Linsen-Brei',
    whyForThisAge:
      'Iron is the priority at 6 months — and red lentils plus sweet potato give iron, fiber, and natural sweetness in one easy puree. Smooth enough for first spoons, freezes in cubes.',
    whyForThisAge_de:
      'Eisen hat ab dem 6. Monat Priorität — rote Linsen und Süsskartoffel liefern Eisen, Ballaststoffe und natürliche Süsse in einem einfachen Brei. Glatt genug für die ersten Löffel, friert prima in Würfeln ein.',
    prepMinutes: 5,
    cookMinutes: 20,
    servings: '6 ice-cube portions',
    servings_de: '6 Eiswürfel-Portionen',
    ingredients: [
      { name: 'sweet potato, peeled and cubed', name_de: 'Süsskartoffel, geschält und gewürfelt', metric: '200 g', household: '1 small', household_de: '1 kleine' },
      { name: 'red lentils, rinsed', name_de: 'rote Linsen, abgespült', metric: '50 g', household: '¼ cup', household_de: '4 EL' },
      { name: 'water', name_de: 'Wasser', metric: '250 ml', household: '1 cup', household_de: '1 Tasse' },
      { name: 'breast milk or formula (to thin)', name_de: 'Muttermilch oder Säuglingsmilch (zum Verdünnen)', metric: '30 ml', household: '2 tbsp', household_de: '2 EL' },
    ],
    steps: [
      { text: 'Combine sweet potato, lentils, and water in a small saucepan.', text_de: 'Süsskartoffel, Linsen und Wasser in einem kleinen Topf zusammengeben.' },
      { text: 'Bring to a simmer, cover, and cook 18–20 minutes until everything is very soft.', text_de: 'Aufkochen lassen, zudecken und 18–20 Minuten köcheln, bis alles ganz weich ist.' },
      { text: 'Blend with an immersion blender until smooth. Add breast milk or formula to reach a smooth, runny consistency.', text_de: 'Mit dem Stabmixer glatt pürieren. Mit Muttermilch oder Säuglingsmilch zur fliessenden, glatten Konsistenz verdünnen.' },
      { text: 'Cool fully, then portion into ice cube trays and freeze. Thaw one cube at a time as needed.', text_de: 'Komplett abkühlen lassen, in Eiswürfelformen portionieren und einfrieren. Bei Bedarf einzelne Würfel auftauen.' },
    ],
    allergens: [],
    notes: [
      'No salt. Iron in lentils + vitamin C in sweet potato boost absorption together.',
      'For a coarser version after 7 months, blend less or mash with a fork.',
    ],
    notes_de: [
      'Kein Salz. Eisen in den Linsen und Vitamin C in der Süsskartoffel verbessern die Aufnahme zusammen.',
      'Für eine gröbere Variante ab 7 Monaten weniger pürieren oder mit der Gabel zerdrücken.',
    ],
  },

  {
    id: 'parsnip-pear-puree-6-8',
    bandId: 'starting-solids',
    dayOfWeek: 'tue',
    title: 'Parsnip & pear puree',
    title_de: 'Pastinaken-Birnen-Brei',
    whyForThisAge:
      'Parsnip is gentler than carrot for first introductions — slightly sweeter, easier on the gut. Paired with pear for a familiar fruit note. A classic European first food.',
    whyForThisAge_de:
      'Pastinaken sind für den Beikoststart sanfter als Karotten — leicht süsslich, gut verträglich. Mit Birne als bekannte Fruchtnote kombiniert. Ein klassischer europäischer Erstbrei.',
    prepMinutes: 5,
    cookMinutes: 15,
    servings: '5 ice-cube portions',
    servings_de: '5 Eiswürfel-Portionen',
    ingredients: [
      { name: 'parsnip, peeled and chopped', name_de: 'Pastinake, geschält und gewürfelt', metric: '150 g', household: '1 medium', household_de: '1 mittlere' },
      { name: 'ripe pear, peeled and cored', name_de: 'reife Birne, geschält und entkernt', metric: '100 g', household: '1 small', household_de: '1 kleine' },
      { name: 'water', name_de: 'Wasser', metric: '150 ml', household: '⅔ cup', household_de: '150 ml' },
    ],
    steps: [
      { text: 'Steam parsnip in 100 ml water for 10 minutes.', text_de: 'Pastinake in 100 ml Wasser 10 Minuten dämpfen.' },
      { text: 'Add pear chunks and steam another 5 minutes until both are fork-tender.', text_de: 'Birnenstücke dazugeben und weitere 5 Minuten dämpfen, bis alles weich ist.' },
      { text: 'Reserve the cooking liquid. Blend pear and parsnip together, adding liquid to reach a smooth texture.', text_de: 'Kochwasser auffangen. Birne und Pastinake zusammen pürieren, Kochwasser zur glatten Textur dazugeben.' },
      { text: 'Cool, portion, freeze. Serve thawed at lukewarm temperature.', text_de: 'Abkühlen lassen, portionieren, einfrieren. Lauwarm servieren.' },
    ],
    allergens: [],
    notes: [
      'Pear keeps things from getting too starchy. The combination is the point.',
      'Single new food rule: introduce parsnip alone for 2 days first if this is the first time, before combining.',
    ],
    notes_de: [
      'Die Birne verhindert, dass es zu stärkehaltig wird. Die Kombination macht\'s aus.',
      'Regel „ein neues Lebensmittel": Pastinake bei der ersten Einführung 2 Tage alleine anbieten, bevor du kombinierst.',
    ],
  },

  {
    id: 'iron-fortified-oat-apple-6-8',
    bandId: 'starting-solids',
    dayOfWeek: 'wed',
    title: 'Iron-fortified oat porridge with apple',
    title_de: 'Eisen-Hafer-Brei mit Apfel',
    whyForThisAge:
      "Iron-fortified infant oatmeal is one of the easiest ways to hit the 6-month iron target. Apple adds vitamin C for absorption. A great first breakfast.",
    whyForThisAge_de:
      'Eisen-angereicherter Hafergetreidebrei ist einer der einfachsten Wege, das Eisen-Ziel ab 6 Monaten zu erreichen. Apfel bringt Vitamin C für die Aufnahme. Ein perfektes erstes Frühstück.',
    prepMinutes: 3,
    cookMinutes: 8,
    servings: '1 portion',
    servings_de: '1 Portion',
    ingredients: [
      { name: 'iron-fortified infant oat cereal', name_de: 'Eisen-angereicherter Hafer-Getreidebrei (z.B. Hipp/Holle)', metric: '15 g', household: '2 tbsp', household_de: '2 EL' },
      { name: 'apple, peeled and grated', name_de: 'Apfel, geschält und fein gerieben', metric: '60 g', household: '½ small', household_de: '½ kleiner' },
      { name: 'water (just-boiled, then cooled to warm)', name_de: 'Wasser (aufgekocht, auf warm abgekühlt)', metric: '60 ml', household: '¼ cup', household_de: '4 EL' },
      { name: 'breast milk or formula', name_de: 'Muttermilch oder Säuglingsmilch', metric: '30 ml', household: '2 tbsp', household_de: '2 EL' },
    ],
    steps: [
      { text: 'Stir oat cereal into warm water until smooth.', text_de: 'Hafer-Getreidebrei in warmes Wasser einrühren, bis er glatt ist.' },
      { text: 'Cook the grated apple in a tiny pan with 1 tbsp water for 3 minutes, until soft and pulpy.', text_de: 'Den geriebenen Apfel in einer kleinen Pfanne mit 1 EL Wasser 3 Minuten kochen, bis er weich und musig ist.' },
      { text: 'Mix apple into the oatmeal. Stir in breast milk or formula to reach a runny consistency.', text_de: 'Apfel unter den Brei mischen. Muttermilch oder Säuglingsmilch einrühren bis zur fliessenden Konsistenz.' },
      { text: 'Check temperature on the inside of your wrist before serving.', text_de: 'Temperatur am Handgelenk testen, bevor du servierst.' },
    ],
    allergens: ['wheat'],
    notes: [
      'Choose an iron-fortified product specifically — most regular oatmeal is not fortified.',
      'Skip honey under 12 months, always.',
    ],
    notes_de: [
      'Achte explizit auf „mit Eisen angereichert" auf der Packung — normale Haferflocken sind das nicht.',
      'Kein Honig vor dem 12. Lebensmonat, niemals.',
    ],
  },

  {
    id: 'avocado-banana-6-8',
    bandId: 'starting-solids',
    dayOfWeek: 'thu',
    title: 'Avocado & banana smash',
    title_de: 'Avocado-Bananen-Mus',
    whyForThisAge:
      'No-cook, ready in 60 seconds. Healthy fats from avocado support brain development. The fattiest first-food option — and toddlers tend to take to it readily.',
    whyForThisAge_de:
      'Kein Kochen, fertig in 60 Sekunden. Gesunde Fette aus der Avocado unterstützen die Hirnentwicklung. Die fettreichste Erstbeikost-Option — und Babys nehmen sie meist gerne an.',
    prepMinutes: 2,
    cookMinutes: 0,
    servings: '1 portion',
    servings_de: '1 Portion',
    ingredients: [
      { name: 'ripe avocado', name_de: 'reife Avocado', metric: '40 g', household: '¼ small', household_de: '¼ kleine' },
      { name: 'ripe banana', name_de: 'reife Banane', metric: '40 g', household: '¼ medium', household_de: '¼ mittlere' },
      { name: 'breast milk or formula (optional, to thin)', name_de: 'Muttermilch oder Säuglingsmilch (optional, zum Verdünnen)', metric: '15 ml', household: '1 tbsp', household_de: '1 EL' },
    ],
    steps: [
      { text: 'Scoop avocado flesh and slice banana into a small bowl.', text_de: 'Avocado-Fruchtfleisch und Bananenscheiben in eine kleine Schüssel geben.' },
      { text: 'Mash thoroughly with a fork until smooth. Add milk if needed to reach desired consistency.', text_de: 'Mit einer Gabel gründlich zerdrücken, bis es glatt ist. Bei Bedarf mit Milch verdünnen.' },
      { text: 'Serve immediately — avocado browns within an hour.', text_de: 'Sofort servieren — Avocado wird innerhalb einer Stunde braun.' },
    ],
    allergens: [],
    notes: [
      'A perfect "rescue meal" when you have nothing prepped — both ingredients are usually on hand.',
      'Not freezer-friendly. Make fresh.',
    ],
    notes_de: [
      'Ein perfektes „Rettungsessen", wenn nichts vorbereitet ist — beide Zutaten sind meistens da.',
      'Nicht zum Einfrieren. Frisch zubereiten.',
    ],
  },

  {
    id: 'carrot-beef-puree-6-8',
    bandId: 'starting-solids',
    dayOfWeek: 'fri',
    title: 'Carrot & beef iron-boost puree',
    title_de: 'Karotten-Rindfleisch-Brei (Eisen-Power)',
    whyForThisAge:
      "Red meat is the most bioavailable iron source — and 6 months is exactly when iron stores from pregnancy run out. This is a classic European Mittagsbrei (lunch puree).",
    whyForThisAge_de:
      'Rotes Fleisch ist die am besten verfügbare Eisenquelle — und mit 6 Monaten gehen die Eisenspeicher aus der Schwangerschaft zur Neige. Das ist der klassische europäische Mittagsbrei.',
    prepMinutes: 5,
    cookMinutes: 30,
    servings: '5 portions (freeze)',
    servings_de: '5 Portionen (zum Einfrieren)',
    ingredients: [
      { name: 'lean beef (cheek or shoulder)', name_de: 'mageres Rindfleisch (Backe oder Schulter)', metric: '50 g', household: '2 oz', household_de: '50 g' },
      { name: 'carrots, peeled and sliced', name_de: 'Karotten, geschält und in Scheiben', metric: '150 g', household: '2 medium', household_de: '2 mittlere' },
      { name: 'potato, peeled and cubed', name_de: 'Kartoffel, geschält und gewürfelt', metric: '100 g', household: '1 small', household_de: '1 kleine' },
      { name: 'rapeseed oil', name_de: 'Rapsöl', metric: '5 ml', household: '1 tsp', household_de: '1 TL' },
      { name: 'water', name_de: 'Wasser', metric: '200 ml', household: '¾ cup', household_de: '200 ml' },
    ],
    steps: [
      { text: 'Cube beef into 1 cm pieces. Add to a small pot with carrots, potato, and water.', text_de: 'Rindfleisch in 1 cm Würfel schneiden. Mit Karotten, Kartoffel und Wasser in einen kleinen Topf geben.' },
      { text: 'Bring to a simmer, cover, and cook 25–30 minutes until beef is very tender.', text_de: 'Aufkochen lassen, zudecken und 25–30 Minuten köcheln, bis das Rindfleisch ganz zart ist.' },
      { text: 'Add rapeseed oil. Blend everything together — the oil helps iron absorption and adds calories.', text_de: 'Rapsöl dazu. Alles zusammen pürieren — das Öl verbessert die Eisenaufnahme und liefert Kalorien.' },
      { text: 'Add cooking liquid as needed to reach a smooth, slightly runny consistency.', text_de: 'Bei Bedarf Kochwasser zur glatten, leicht fliessenden Konsistenz dazugeben.' },
      { text: 'Cool, portion into 4 containers, freeze. Use within 4 weeks.', text_de: 'Abkühlen, in 4 Behälter portionieren, einfrieren. Innerhalb von 4 Wochen verbrauchen.' },
    ],
    allergens: [],
    notes: [
      'The classic Mittagsbrei: meat + vegetable + potato + oil. Hits ~1 mg iron per portion.',
      'Adding a few drops of orange or apple juice to the warm puree at serving boosts iron absorption further.',
    ],
    notes_de: [
      'Der klassische Mittagsbrei: Fleisch + Gemüse + Kartoffel + Öl. Liefert ca. 1 mg Eisen pro Portion.',
      'Ein paar Tropfen Orangen- oder Apfelsaft beim Servieren in den warmen Brei verbessert die Eisenaufnahme zusätzlich.',
    ],
  },

  {
    id: 'pumpkin-millet-6-8',
    bandId: 'starting-solids',
    dayOfWeek: 'sat',
    title: 'Hokkaido & millet porridge',
    title_de: 'Hokkaido-Hirse-Brei',
    whyForThisAge:
      'Millet is gluten-free and rich in iron, magnesium, and B-vitamins — a quiet powerhouse. Pumpkin is naturally sweet and gentle. A great rotation away from oats.',
    whyForThisAge_de:
      'Hirse ist glutenfrei und reich an Eisen, Magnesium und B-Vitaminen — ein stiller Kraftspender. Hokkaido ist natürlich süss und gut verträglich. Eine gute Abwechslung zum Hafer.',
    prepMinutes: 5,
    cookMinutes: 20,
    servings: '4 portions',
    servings_de: '4 Portionen',
    ingredients: [
      { name: 'hulled millet, rinsed', name_de: 'Hirse (geschält), abgespült', metric: '40 g', household: '3 tbsp', household_de: '3 EL' },
      { name: 'Hokkaido pumpkin, deseeded', name_de: 'Hokkaido-Kürbis, entkernt', metric: '200 g', household: '½ small', household_de: '½ kleiner' },
      { name: 'water', name_de: 'Wasser', metric: '300 ml', household: '1¼ cups', household_de: '300 ml' },
      { name: 'rapeseed oil', name_de: 'Rapsöl', metric: '5 ml', household: '1 tsp', household_de: '1 TL' },
    ],
    steps: [
      { text: 'Cut pumpkin (skin stays on with Hokkaido) into 2 cm cubes.', text_de: 'Kürbis (die Schale bleibt beim Hokkaido dran) in 2 cm Würfel schneiden.' },
      { text: 'Combine millet, pumpkin, and water in a saucepan. Simmer covered for 18–20 minutes.', text_de: 'Hirse, Kürbis und Wasser in einem Topf zusammengeben. Zugedeckt 18–20 Minuten köcheln.' },
      { text: 'Add rapeseed oil and blend smooth.', text_de: 'Rapsöl dazugeben und glatt pürieren.' },
      { text: 'Cool, portion, freeze in cubes.', text_de: 'Abkühlen, in Würfeln portionieren und einfrieren.' },
    ],
    allergens: [],
    notes: [
      'Hokkaido is one of the rare squashes where the skin softens completely — no peeling needed.',
      'Try replacing pumpkin with sweet potato or carrot for variation.',
    ],
    notes_de: [
      'Hokkaido ist einer der wenigen Kürbisse, deren Schale beim Kochen komplett weich wird — kein Schälen nötig.',
      'Versuch auch Süsskartoffel oder Karotte statt Kürbis zur Abwechslung.',
    ],
  },

  {
    id: 'peanut-banana-intro-6-8',
    bandId: 'starting-solids',
    dayOfWeek: 'sun',
    title: 'Peanut butter & banana intro',
    title_de: 'Erdnuss-Bananen-Einführung',
    whyForThisAge:
      'Peanut is one of the highest-priority early allergens — LEAP study evidence and DGKJ/AAP recommendations both say introduce by 6 months. Smooth peanut butter thinned with banana mash is the classic safe-texture way.',
    whyForThisAge_de:
      'Erdnuss ist eines der wichtigsten frühen Allergene — die LEAP-Studie und Empfehlungen von DGKJ und AAP sprechen sich klar für die Einführung bis 6 Monate aus. Glattes Erdnussmus, mit Bananenmus verdünnt, ist die klassische sichere Methode.',
    prepMinutes: 3,
    cookMinutes: 0,
    servings: '1 portion',
    servings_de: '1 Portion',
    ingredients: [
      { name: 'smooth peanut butter (no sugar, no salt)', name_de: 'glattes Erdnussmus (ohne Zucker, ohne Salz)', metric: '5 g', household: '1 tsp', household_de: '1 TL' },
      { name: 'ripe banana, mashed', name_de: 'reife Banane, zerdrückt', metric: '60 g', household: '½ medium', household_de: '½ mittlere' },
      { name: 'warm water or breast milk', name_de: 'warmes Wasser oder Muttermilch', metric: '15 ml', household: '1 tbsp', household_de: '1 EL' },
    ],
    steps: [
      { text: 'Mash banana very smoothly in a small bowl.', text_de: 'Banane in einer kleinen Schüssel ganz glatt zerdrücken.' },
      { text: 'Add peanut butter and warm water. Mix until completely smooth — no peanut butter clumps.', text_de: 'Erdnussmus und warmes Wasser dazugeben. Glatt rühren — keine Erdnussmus-Klumpen.' },
      { text: 'Offer first thing in the morning at home, when you can monitor for any reaction over the next 2 hours.', text_de: 'Morgens zuhause anbieten, damit du in den nächsten 2 Stunden auf eventuelle Reaktionen achten kannst.' },
      { text: 'Watch for: face swelling, hives, vomiting, breathing trouble. If any → call 112 (DE/AT) / 144 (CH).', text_de: 'Achte auf: Gesichts-Schwellung, Quaddeln, Erbrechen, Atemnot. Bei einem dieser Zeichen → 112 (DE/AT) oder 144 (CH) anrufen.' },
    ],
    allergens: ['peanut'],
    notes: [
      'Critical: smooth peanut butter only. Whole peanuts and crunchy peanut butter are choking hazards under 4.',
      'After the first exposure with no reaction, offer 1× per week to maintain tolerance per LEAP guidance.',
      'If your family has strong allergy history or your baby has severe eczema, talk to your pediatrician first.',
    ],
    notes_de: [
      'Wichtig: nur glattes Erdnussmus. Ganze Erdnüsse und stückiges Erdnussmus sind unter 4 Jahren Verschluckungsgefahr.',
      'Nach der ersten Einführung ohne Reaktion 1× pro Woche anbieten, um die Toleranz nach LEAP-Empfehlung zu erhalten.',
      'Bei stark belasteter Allergie-Familienanamnese oder schwerem Ekzem vorher mit der Kinderärztin sprechen.',
    ],
  },

  // ╔══════════════════════════════════════════════════════════════╗
  // ║ Stage: 8–12 months · Fingerfood                             ║
  // ║ Self-feeding, pincer-grasp practice, soft textures.         ║
  // ╚══════════════════════════════════════════════════════════════╝

  {
    id: 'banana-oat-fingers-8-12',
    bandId: 'finger-foods',
    dayOfWeek: 'mon',
    title: 'Banana-oat finger strips (no-sugar)',
    title_de: 'Bananen-Hafer-Streifen (ohne Zucker)',
    whyForThisAge:
      'Soft enough to gum, firm enough to grip. The shape teaches pincer-grasp. Banana sweetens naturally — no sugar needed. Freezes brilliantly.',
    whyForThisAge_de:
      'Weich genug zum Kauen mit dem Zahnfleisch, fest genug zum Greifen. Die Form trainiert den Pinzettengriff. Banane süsst natürlich — kein Zucker nötig. Lässt sich super einfrieren.',
    prepMinutes: 5,
    cookMinutes: 15,
    servings: '12 strips',
    servings_de: '12 Streifen',
    ingredients: [
      { name: 'ripe banana, mashed', name_de: 'reife Banane, zerdrückt', metric: '180 g', household: '2 small', household_de: '2 kleine' },
      { name: 'rolled oats', name_de: 'Haferflocken', metric: '80 g', household: '¾ cup', household_de: '8 EL' },
      { name: 'large egg', name_de: 'Ei (Grösse M)', metric: '50 g', household: '1', household_de: '1' },
      { name: 'cinnamon (optional)', name_de: 'Zimt (optional)', metric: '0.5 g', household: 'small pinch', household_de: 'kleine Prise' },
    ],
    steps: [
      { text: 'Heat oven to 180°C / 350°F. Line a baking tray with parchment.', text_de: 'Ofen auf 180 °C vorheizen. Backblech mit Backpapier auslegen.' },
      { text: 'Mix mashed banana, oats, egg, and cinnamon in a bowl. Let sit 5 minutes so oats absorb moisture.', text_de: 'Zerdrückte Banane, Haferflocken, Ei und Zimt in einer Schüssel mischen. 5 Minuten ruhen lassen, damit die Haferflocken Feuchtigkeit aufnehmen.' },
      { text: 'Spread evenly on the tray to about 1.5 cm thickness. Bake 12–15 minutes until firm and golden.', text_de: 'Gleichmässig auf dem Blech auf etwa 1,5 cm Dicke ausstreichen. 12–15 Minuten backen, bis fest und goldbraun.' },
      { text: 'Cool, then cut into finger-length strips (about 8 cm long).', text_de: 'Abkühlen lassen, dann in fingerlange Streifen schneiden (etwa 8 cm lang).' },
    ],
    allergens: ['egg', 'wheat'],
    notes: [
      'Freezes 2 months. Reheat 30 seconds in the microwave or 2 minutes in a toaster.',
      'No sugar. The banana does all the sweetening at this age.',
    ],
    notes_de: [
      '2 Monate im Gefrierfach. 30 Sekunden in der Mikrowelle oder 2 Minuten im Toaster aufwärmen.',
      'Kein Zucker. Die Banane übernimmt das Süssen in diesem Alter komplett.',
    ],
  },

  {
    id: 'cheesy-potato-balls-8-12',
    bandId: 'finger-foods',
    dayOfWeek: 'tue',
    title: 'Cheddar-potato pincer balls',
    title_de: 'Cheddar-Kartoffel-Bällchen',
    whyForThisAge:
      'Walnut-sized, soft, and rollable — perfect pincer-grasp practice. Cheese for calcium and fat. A cooked, dairy-introduction food.',
    whyForThisAge_de:
      'Walnussgross, weich, rund — ideal zum Üben des Pinzettengriffs. Käse liefert Kalzium und Fett. Eine gegarte Variante zur Milchprodukt-Einführung.',
    prepMinutes: 10,
    cookMinutes: 25,
    servings: '12 balls',
    servings_de: '12 Bällchen',
    ingredients: [
      { name: 'floury potatoes, peeled', name_de: 'mehlig kochende Kartoffeln, geschält', metric: '250 g', household: '2 medium', household_de: '2 mittlere' },
      { name: 'cheddar (or mature Gouda), grated', name_de: 'Cheddar (oder mittelalter Gouda), gerieben', metric: '50 g', household: '½ cup', household_de: '½ Tasse' },
      { name: 'large egg yolk', name_de: 'Eigelb (Grösse M)', metric: '15 g', household: '1', household_de: '1' },
      { name: 'whole-wheat breadcrumbs', name_de: 'Vollkornsemmelbrösel', metric: '20 g', household: '2 tbsp', household_de: '2 EL' },
    ],
    steps: [
      { text: 'Boil potatoes 15 minutes until very soft. Drain and mash dry — no milk needed.', text_de: 'Kartoffeln 15 Minuten kochen, bis sie ganz weich sind. Abgiessen und ohne Milch trocken zerstampfen.' },
      { text: 'Mix in cheese, egg yolk, and half the breadcrumbs. Cool 5 minutes.', text_de: 'Käse, Eigelb und die Hälfte der Semmelbrösel unterrühren. 5 Minuten abkühlen.' },
      { text: 'Roll walnut-sized balls, then roll in remaining breadcrumbs.', text_de: 'Walnussgrosse Bällchen formen, in den restlichen Semmelbröseln wälzen.' },
      { text: 'Bake at 200°C / 400°F for 12 minutes until lightly golden.', text_de: '12 Minuten bei 200 °C backen, bis sie leicht goldbraun sind.' },
    ],
    allergens: ['dairy', 'egg', 'wheat'],
    notes: [
      'Squeeze one between your fingers — should compress easily. If hard, it\'s too crunchy for under-1.',
      'Freezes 2 months. Reheat from frozen 12 minutes at 180°C.',
    ],
    notes_de: [
      'Drück eines zwischen den Fingern — soll sich leicht eindrücken lassen. Wenn hart, ist es zu knusprig für unter 1 Jahr.',
      '2 Monate im Gefrierfach. Aus gefroren 12 Minuten bei 180 °C aufwärmen.',
    ],
  },

  {
    id: 'broccoli-trees-8-12',
    bandId: 'finger-foods',
    dayOfWeek: 'wed',
    title: 'Steamed broccoli "trees"',
    title_de: 'Gedämpfte Brokkoli-„Bäumchen"',
    whyForThisAge:
      'The stem is the natural handle, the florets get gummed off. Soft enough that no teeth needed. Iron + vitamin K + the bitter-taste exposure that locks in vegetable acceptance long-term.',
    whyForThisAge_de:
      'Der Stiel ist der natürliche Griff, die Röschen werden mit dem Zahnfleisch abgekaut. Weich genug, dass keine Zähne nötig sind. Eisen, Vitamin K — und die bitteren Geschmäcker, die langfristig Gemüse-Akzeptanz prägen.',
    prepMinutes: 3,
    cookMinutes: 8,
    servings: '6 trees',
    servings_de: '6 Bäumchen',
    ingredients: [
      { name: 'broccoli, large florets with stem', name_de: 'Brokkoli, grosse Röschen mit Stiel', metric: '200 g', household: '½ head', household_de: '½ Kopf' },
      { name: 'olive oil', name_de: 'Olivenöl', metric: '5 ml', household: '1 tsp', household_de: '1 TL' },
    ],
    steps: [
      { text: 'Cut broccoli into "trees" — keep enough stem (3–4 cm) so baby can hold the handle.', text_de: 'Brokkoli in „Bäumchen" schneiden — genug Stiel dranlassen (3–4 cm), damit das Baby es greifen kann.' },
      { text: 'Steam 6–8 minutes until completely soft when poked with a fork. Florets must be soft.', text_de: '6–8 Minuten dämpfen, bis alles weich ist (Gabeltest). Die Röschen müssen wirklich weich sein.' },
      { text: 'Toss warm broccoli with olive oil. Cool to lukewarm before serving.', text_de: 'Warmen Brokkoli mit Olivenöl beträufeln. Auf lauwarm abkühlen lassen.' },
    ],
    allergens: [],
    notes: [
      "Don't be alarmed if baby green-stained for a day — broccoli's effect on poop is dramatic but normal.",
      "Babies often refuse broccoli the first 5–10 times. Keep offering — repetition is the actual mechanism.",
    ],
    notes_de: [
      'Erschrick nicht, wenn das Baby einen Tag lang grün im Stuhl ist — die Wirkung von Brokkoli ist dramatisch, aber normal.',
      'Babys lehnen Brokkoli die ersten 5–10 Mal oft ab. Weiter anbieten — Wiederholung ist der eigentliche Mechanismus.',
    ],
  },

  {
    id: 'hummus-toast-fingers-8-12',
    bandId: 'finger-foods',
    dayOfWeek: 'thu',
    title: 'Hummus on whole-grain toast strips',
    title_de: 'Hummus auf Vollkorn-Toaststreifen',
    whyForThisAge:
      'Sesame paste (tahini) in hummus is one of the top 9 allergens worth introducing early. Chickpeas add iron and protein. The toast gives a handle. Three jobs in one snack.',
    whyForThisAge_de:
      'Sesammus (Tahini) im Hummus gehört zu den 9 wichtigen Allergenen für die frühe Einführung. Kichererbsen bringen Eisen und Eiweiss. Der Toast gibt einen Griff. Drei Jobs in einem Snack.',
    prepMinutes: 5,
    cookMinutes: 0,
    servings: '2 portions',
    servings_de: '2 Portionen',
    ingredients: [
      { name: 'cooked chickpeas (drained from a can)', name_de: 'gekochte Kichererbsen (aus der Dose abgegossen)', metric: '100 g', household: '½ cup', household_de: '½ Tasse' },
      { name: 'tahini (sesame paste)', name_de: 'Tahini (Sesammus)', metric: '15 g', household: '1 tbsp', household_de: '1 EL' },
      { name: 'lemon juice', name_de: 'Zitronensaft', metric: '5 ml', household: '1 tsp', household_de: '1 TL' },
      { name: 'olive oil', name_de: 'Olivenöl', metric: '10 ml', household: '2 tsp', household_de: '2 TL' },
      { name: 'whole-grain toast', name_de: 'Vollkorntoast', metric: '50 g', household: '2 slices', household_de: '2 Scheiben' },
    ],
    steps: [
      { text: 'Blend chickpeas, tahini, lemon, and olive oil with 2 tbsp water until smooth. No salt added.', text_de: 'Kichererbsen, Tahini, Zitrone und Olivenöl mit 2 EL Wasser glatt pürieren. Kein Salz.' },
      { text: 'Lightly toast the bread. Spread thinly with hummus.', text_de: 'Brot leicht toasten. Dünn mit Hummus bestreichen.' },
      { text: 'Cut into thumb-wide strips (4–5 per slice).', text_de: 'In daumenbreite Streifen schneiden (4–5 pro Scheibe).' },
    ],
    allergens: ['sesame', 'wheat'],
    notes: [
      'Skip garlic for under-1 — too sharp. Add to the adult portion separately.',
      'Hummus keeps 4 days in the fridge.',
    ],
    notes_de: [
      'Kein Knoblauch unter 1 Jahr — zu scharf. Für die Erwachsenen-Portion separat dazu.',
      'Hummus hält 4 Tage im Kühlschrank.',
    ],
  },

  {
    id: 'sweet-potato-wedges-8-12',
    bandId: 'finger-foods',
    dayOfWeek: 'fri',
    title: 'Baked sweet potato wedges',
    title_de: 'Süsskartoffel-Spalten aus dem Ofen',
    whyForThisAge:
      'Soft, sweet, easy to grip. Beta-carotene boosts the immune system and supports developing eyesight. Naturally salt-free flavor that babies love.',
    whyForThisAge_de:
      'Weich, süss, gut zu greifen. Beta-Carotin unterstützt das Immunsystem und die Augenentwicklung. Natürlich salzfreier Geschmack, den Babys mögen.',
    prepMinutes: 5,
    cookMinutes: 25,
    servings: '3 portions',
    servings_de: '3 Portionen',
    ingredients: [
      { name: 'sweet potato, scrubbed', name_de: 'Süsskartoffel, gewaschen', metric: '300 g', household: '1 large', household_de: '1 grosse' },
      { name: 'olive oil', name_de: 'Olivenöl', metric: '10 ml', household: '2 tsp', household_de: '2 TL' },
      { name: 'cinnamon (optional)', name_de: 'Zimt (optional)', metric: '0.5 g', household: 'pinch', household_de: 'Prise' },
    ],
    steps: [
      { text: 'Heat oven to 200°C / 400°F. Cut sweet potato into 2 cm wedges, skin on.', text_de: 'Ofen auf 200 °C vorheizen. Süsskartoffel mit Schale in 2 cm dicke Spalten schneiden.' },
      { text: 'Toss with olive oil and optional cinnamon. Spread on a baking tray.', text_de: 'Mit Olivenöl und optional Zimt vermengen. Auf einem Backblech ausbreiten.' },
      { text: 'Bake 22–25 minutes, turning once. They should be very soft when squeezed.', text_de: '22–25 Minuten backen, einmal wenden. Sie sollen sich beim Andrücken ganz weich anfühlen.' },
      { text: 'Cool until warm — pulp inside stays hot longer than the outside suggests.', text_de: 'Auf warm abkühlen — innen bleibt es länger heiss, als es aussen aussieht.' },
    ],
    allergens: [],
    notes: [
      'No salt under 12 months. The natural sweetness is plenty.',
      "Baby will probably eat the soft inside and leave the skin. That's fine — the skin gives them something to hold.",
    ],
    notes_de: [
      'Kein Salz unter 12 Monaten. Die natürliche Süsse reicht völlig.',
      'Das Baby wird wahrscheinlich das weiche Innere essen und die Schale liegen lassen. Okay — die Schale gibt ihm etwas zum Festhalten.',
    ],
  },

  {
    id: 'scrambled-egg-avocado-8-12',
    bandId: 'finger-foods',
    dayOfWeek: 'sat',
    title: 'Soft scramble with avocado strips',
    title_de: 'Cremiges Rührei mit Avocado-Streifen',
    whyForThisAge:
      'Eggs are a top early-allergen food worth offering regularly — and a complete protein. The soft scramble texture is gentle on gums. Avocado adds healthy fats.',
    whyForThisAge_de:
      'Ei gehört zu den wichtigen frühen Allergenen, die regelmässig angeboten werden sollten — und ist ein vollwertiges Eiweiss. Die cremige Rührei-Textur ist sanft zum Zahnfleisch. Avocado bringt gesunde Fette.',
    prepMinutes: 3,
    cookMinutes: 5,
    servings: '1 portion',
    servings_de: '1 Portion',
    ingredients: [
      { name: 'large egg', name_de: 'Ei (Grösse M)', metric: '50 g', household: '1', household_de: '1' },
      { name: 'whole milk', name_de: 'Vollmilch (3,5 %)', metric: '15 ml', household: '1 tbsp', household_de: '1 EL' },
      { name: 'butter', name_de: 'Butter', metric: '5 g', household: '1 tsp', household_de: '1 TL' },
      { name: 'ripe avocado', name_de: 'reife Avocado', metric: '40 g', household: '¼ small', household_de: '¼ kleine' },
    ],
    steps: [
      { text: 'Whisk egg and milk in a small bowl.', text_de: 'Ei und Milch in einer kleinen Schüssel verquirlen.' },
      { text: 'Melt butter in a small pan over low heat. Pour in egg.', text_de: 'Butter in einer kleinen Pfanne bei niedriger Hitze schmelzen. Ei dazugeben.' },
      { text: 'Stir gently and constantly until just set — should be wet and very soft, not dry.', text_de: 'Sanft und ständig rühren, bis es gerade gestockt ist — soll feucht und ganz weich sein, nicht trocken.' },
      { text: 'Slice avocado into thumb-thick strips. Serve alongside.', text_de: 'Avocado in daumendicke Streifen schneiden. Daneben anrichten.' },
    ],
    allergens: ['egg', 'dairy'],
    notes: [
      'Cook eggs fully (no runny yolk) for under-1. After 1 year, slightly less cooked is okay.',
      'Skip salt completely.',
    ],
    notes_de: [
      'Ei für unter 1 Jahr ganz durchgaren (kein flüssiges Eigelb). Nach dem 1. Geburtstag darf es etwas weicher sein.',
      'Komplett ohne Salz.',
    ],
  },

  {
    id: 'mini-chicken-balls-8-12',
    bandId: 'finger-foods',
    dayOfWeek: 'sun',
    title: 'Iron-rich mini chicken balls',
    title_de: 'Eisenreiche Mini-Hähnchenbällchen',
    whyForThisAge:
      'Ground chicken thigh has more iron than breast and stays moist. Walnut-sized for easy grip. A mealtime alternative to puree, with the iron load babies need at this age.',
    whyForThisAge_de:
      'Gehacktes Hähnchen-Oberschenkelfleisch hat mehr Eisen als die Brust und bleibt saftig. Walnussgross zum Greifen. Eine Alternative zum Brei mit der Eisenmenge, die Babys in diesem Alter brauchen.',
    prepMinutes: 10,
    cookMinutes: 15,
    servings: '10 balls',
    servings_de: '10 Bällchen',
    ingredients: [
      { name: 'ground chicken (preferably thigh)', name_de: 'Hähnchenhack (vorzugsweise vom Schenkel)', metric: '200 g', household: '7 oz', household_de: '200 g' },
      { name: 'zucchini, finely grated and squeezed dry', name_de: 'Zucchini, fein gerieben und ausgedrückt', metric: '60 g', household: '½ small', household_de: '½ kleine' },
      { name: 'breadcrumbs (whole-grain)', name_de: 'Vollkorn-Semmelbrösel', metric: '20 g', household: '2 tbsp', household_de: '2 EL' },
      { name: 'egg yolk', name_de: 'Eigelb', metric: '15 g', household: '1', household_de: '1' },
      { name: 'olive oil (for tray)', name_de: 'Olivenöl (für das Blech)', metric: '5 ml', household: '1 tsp', household_de: '1 TL' },
    ],
    steps: [
      { text: 'Heat oven to 200°C / 400°F. Brush a baking tray with olive oil.', text_de: 'Ofen auf 200 °C vorheizen. Backblech mit Olivenöl bestreichen.' },
      { text: 'Mix chicken, squeezed zucchini, breadcrumbs, and egg yolk in a bowl.', text_de: 'Hähnchenhack, ausgedrückte Zucchini, Semmelbrösel und Eigelb in einer Schüssel mischen.' },
      { text: 'Roll into walnut-sized balls (about 10). Place on tray with space between.', text_de: 'Walnussgrosse Bällchen formen (etwa 10). Mit Abstand auf das Blech setzen.' },
      { text: 'Bake 14–16 minutes until cooked through (insert a knife — clear juices, no pink inside).', text_de: '14–16 Minuten backen, bis sie durch sind (Messertest — klare Flüssigkeit, innen nicht mehr rosa).' },
      { text: 'Cool until warm before serving. Babies love the round shape.', text_de: 'Auf warm abkühlen lassen vor dem Servieren. Babys lieben die runde Form.' },
    ],
    allergens: ['egg', 'wheat'],
    notes: [
      'Press one between fingers — should compress easily. If firm, undercook next time by 1 min.',
      'Freezes 2 months. Reheat from frozen 12 min at 180°C.',
    ],
    notes_de: [
      'Drück eines zwischen den Fingern — soll sich leicht eindrücken lassen. Wenn fest, beim nächsten Mal 1 Min weniger backen.',
      '2 Monate im Gefrierfach. Aus gefroren 12 Min bei 180 °C aufwärmen.',
    ],
  },

  // ╔══════════════════════════════════════════════════════════════╗
  // ║ Stage: 12–18 months · Table food                            ║
  // ║ Family meals, whole-milk transition, picky-eater proof.     ║
  // ║ (KEEPING the 7 existing recipes from v1.4)                  ║
  // ╚══════════════════════════════════════════════════════════════╝

  {
    id: 'oat-banana-pancakes-12-18',
    bandId: 'one-year',
    dayOfWeek: 'mon',
    title: 'Oat-banana pancakes',
    title_de: 'Hafer-Bananen-Pfannkuchen',
    whyForThisAge:
      'Self-feeding territory. The pancakes are firm enough to grip, soft enough to gum, and naturally sweet — no added sugar needed at this age.',
    whyForThisAge_de:
      'Selbstständiges Essen ist jetzt das Thema. Die Pfannkuchen sind fest genug zum Greifen, weich genug für das Kauen mit dem Zahnfleisch, und natürlich süss — kein Zucker nötig in diesem Alter.',
    prepMinutes: 5,
    cookMinutes: 10,
    servings: '4 toddler portions (freezes well)',
    servings_de: '4 Kleinkind-Portionen (lässt sich gut einfrieren)',
    ingredients: [
      { name: 'ripe banana, mashed', name_de: 'reife Banane, zerdrückt', metric: '120 g', household: '1 medium', household_de: '1 mittlere' },
      { name: 'rolled oats, blended into flour', name_de: 'Haferflocken, fein gemahlen', metric: '60 g', household: '⅔ cup', household_de: '6 EL' },
      { name: 'large egg', name_de: 'Ei (Grösse M)', metric: '50 g', household: '1', household_de: '1' },
      { name: 'whole milk', name_de: 'Vollmilch (3,5 % Fett)', metric: '60 ml', household: '¼ cup', household_de: '4 EL' },
      { name: 'cinnamon (optional)', name_de: 'Zimt (optional)', metric: '0.5 g', household: 'small pinch', household_de: 'kleine Prise' },
      { name: 'butter, for pan', name_de: 'Butter für die Pfanne', metric: '5 g', household: '1 tsp', household_de: '1 TL' },
    ],
    steps: [
      { text: 'Blitz the rolled oats in a blender or food processor until they look like flour (about 20 seconds).', text_de: 'Die Haferflocken im Mixer oder in der Küchenmaschine etwa 20 Sekunden mahlen, bis sie wie Mehl aussehen.' },
      { text: 'Whisk the mashed banana, egg, milk, oat flour and cinnamon together until smooth.', text_de: 'Zerdrückte Banane, Ei, Milch, Hafermehl und Zimt glatt verrühren.' },
      { text: 'Heat a non-stick pan over medium-low. Melt the butter to grease the surface.', text_de: 'Beschichtete Pfanne auf mittlerer Hitze erwärmen. Butter darin schmelzen und die Pfanne damit einfetten.' },
      { text: 'Drop tablespoons of batter into the pan. Cook 2 minutes per side, until golden and firm.', text_de: 'Esslöffelweise Teig in die Pfanne geben. Je 2 Minuten pro Seite backen, bis goldbraun und fest.' },
      { text: 'Cool fully before serving to a toddler. Cut into strips for self-feeding.', text_de: 'Vor dem Servieren komplett auskühlen lassen. In Streifen schneiden, damit das Kleinkind selber essen kann.' },
    ],
    allergens: ['egg', 'dairy'],
    notes: [
      'Freezes beautifully — stack with parchment between, freeze flat, then bag.',
      'Reheats from frozen in 30 seconds in the microwave or 2 minutes in a toaster.',
      'No salt or sugar. Banana does the sweetening.',
    ],
    notes_de: [
      'Lässt sich super einfrieren — mit Backpapier dazwischen flach einfrieren, dann in einen Beutel umfüllen.',
      'Aus dem Gefrierfach in 30 Sekunden in der Mikrowelle oder 2 Minuten im Toaster aufgewärmt.',
      'Kein Salz, kein Zucker. Die Banane übernimmt das Süssen.',
    ],
  },
  {
    id: 'mini-frittata-cups-12-18',
    bandId: 'one-year',
    dayOfWeek: 'tue',
    title: 'Mini frittata cups',
    title_de: 'Mini-Frittata-Förmchen',
    whyForThisAge:
      'Eggs are a top early-allergen food the AAP recommends introducing often. Cups are toddler-portion-sized and the muffin tin shape feels important to small humans.',
    whyForThisAge_de:
      'Ei gehört zu den wichtigen früh einzuführenden Allergenen — DGKJ und LEAP-Studie empfehlen regelmässige Einführung. Die Muffin-Form ist genau die richtige Grösse für Kleinkinder und sieht auch noch wichtig aus.',
    prepMinutes: 10,
    cookMinutes: 20,
    servings: '6 frittata cups (freeze the rest)',
    servings_de: '6 Frittata-Förmchen (Rest einfrieren)',
    ingredients: [
      { name: 'large eggs', name_de: 'Eier (Grösse M)', metric: '200 g', household: '4', household_de: '4' },
      { name: 'whole milk', name_de: 'Vollmilch (3,5 %)', metric: '60 ml', household: '¼ cup', household_de: '4 EL' },
      { name: 'baby spinach, finely chopped', name_de: 'Babyspinat, fein gehackt', metric: '40 g', household: 'large handful', household_de: 'grosse Handvoll' },
      { name: 'zucchini, finely diced', name_de: 'Zucchini, fein gewürfelt', metric: '60 g', household: '½ small', household_de: '½ kleine' },
      { name: 'cheddar cheese, grated', name_de: 'Cheddar (oder mittelalter Gouda), gerieben', metric: '40 g', household: '⅓ cup', household_de: '3 EL' },
      { name: 'butter or olive oil, for tin', name_de: 'Butter oder Olivenöl für die Form', metric: '5 g', household: '1 tsp', household_de: '1 TL' },
    ],
    steps: [
      { text: 'Heat oven to 350°F (175°C). Grease a 6-cup muffin tin.', text_de: 'Ofen auf 175 °C vorheizen. Ein 6er-Muffinblech einfetten.' },
      { text: 'Whisk eggs and milk in a bowl until just combined.', text_de: 'Eier und Milch in einer Schüssel kurz verquirlen, bis es gerade verbunden ist.' },
      { text: 'Stir in the chopped spinach, diced zucchini, and grated cheese.', text_de: 'Gehackten Spinat, Zucchiniwürfel und geriebenen Käse unterrühren.' },
      { text: 'Pour into the muffin tin, filling each cup about ¾ full.', text_de: 'In die Muffinform giessen, jede Mulde etwa zu ¾ füllen.' },
      { text: 'Bake 18–20 minutes, until set and slightly puffed.', text_de: '18–20 Minuten backen, bis die Frittata fest und leicht aufgegangen ist.' },
      { text: 'Cool 5 minutes before serving — the inside stays hot longer than the outside suggests.', text_de: '5 Minuten abkühlen lassen vor dem Servieren — innen bleibt es länger heiss, als es aussen aussieht.' },
    ],
    allergens: ['egg', 'dairy'],
    notes: [
      'Refrigerates 4 days; freezes 2 months wrapped individually.',
      'Swap the veg for whatever soft veg you have — bell pepper, peas, mushroom.',
      'Some toddlers will pick out the spinach. That is fine. The exposure still counts.',
    ],
    notes_de: [
      'Im Kühlschrank 4 Tage haltbar; einzeln eingewickelt 2 Monate im Gefrierfach.',
      'Statt Spinat und Zucchini geht jedes weiche Gemüse — Paprika, Erbsen, Champignons.',
      'Manche Kleinkinder picken den Spinat raus. Auch okay. Die Allergen- und Geschmacksexposition zählt trotzdem.',
    ],
  },
  {
    id: 'lentil-tomato-pasta-12-18',
    bandId: 'one-year',
    dayOfWeek: 'wed',
    title: 'Hidden-lentil tomato pasta',
    title_de: 'Versteckte-Linsen-Tomaten-Nudeln',
    whyForThisAge:
      'Iron is still a focus in the second year — anemia screening happens at 12 and 24 months. Red lentils blend invisibly into tomato sauce and add real iron and protein without changing the flavor toddlers already accept.',
    whyForThisAge_de:
      'Eisen bleibt im zweiten Lebensjahr ein Fokus — bei der U6 und U7 wird der Hämoglobinwert kontrolliert. Rote Linsen verschwinden unsichtbar in der Tomatensauce und bringen echtes Eisen und Eiweiss, ohne den Geschmack zu verändern, den das Kleinkind schon akzeptiert.',
    prepMinutes: 5,
    cookMinutes: 25,
    servings: '4 toddler portions',
    servings_de: '4 Kleinkind-Portionen',
    ingredients: [
      { name: 'red lentils, rinsed', name_de: 'rote Linsen, abgespült', metric: '60 g', household: '⅓ cup', household_de: '4 EL' },
      { name: 'crushed tomatoes', name_de: 'gehackte Tomaten (aus der Dose)', metric: '400 g', household: '1 can (14 oz)', household_de: '1 Dose (400 g)' },
      { name: 'water', name_de: 'Wasser', metric: '200 ml', household: '¾ cup', household_de: '200 ml' },
      { name: 'olive oil', name_de: 'Olivenöl', metric: '15 ml', household: '1 tbsp', household_de: '1 EL' },
      { name: 'garlic clove, minced', name_de: 'Knoblauchzehe, fein gehackt', metric: '3 g', household: '1 small', household_de: '1 kleine' },
      { name: 'small pasta (orzo, ditalini, or small shells)', name_de: 'kleine Nudeln (Risoni, Ditalini oder kleine Muschelnudeln)', metric: '120 g', household: '4 oz dry', household_de: '120 g roh' },
      { name: 'parmesan, grated (optional)', name_de: 'Parmesan, gerieben (optional)', metric: '15 g', household: '2 tbsp', household_de: '2 EL' },
    ],
    steps: [
      { text: 'Heat olive oil in a saucepan over medium heat. Add minced garlic and stir 30 seconds.', text_de: 'Olivenöl in einem Topf bei mittlerer Hitze erwärmen. Knoblauch dazugeben und 30 Sekunden unter Rühren anschwitzen.' },
      { text: 'Add the rinsed red lentils, crushed tomatoes, and water. Bring to a simmer.', text_de: 'Abgespülte Linsen, gehackte Tomaten und Wasser zugeben. Aufkochen lassen.' },
      { text: 'Cover and cook 18–20 minutes, stirring once or twice, until lentils are completely soft.', text_de: 'Zugedeckt 18–20 Minuten köcheln lassen, ein- bis zweimal umrühren, bis die Linsen komplett weich sind.' },
      { text: 'Meanwhile, cook pasta in a separate pot per package directions. Drain.', text_de: 'Inzwischen die Nudeln in einem zweiten Topf nach Packungsanweisung kochen. Abgiessen.' },
      { text: 'Blend the sauce with an immersion blender until smooth — the lentils disappear.', text_de: 'Die Sauce mit dem Stabmixer pürieren, bis sie glatt ist — die Linsen verschwinden komplett.' },
      { text: 'Toss the sauce with the pasta. Top with parmesan if your toddler tolerates dairy.', text_de: 'Sauce mit den Nudeln vermengen. Wenn das Kleinkind Milchprodukte verträgt, mit Parmesan bestreuen.' },
    ],
    allergens: ['wheat', 'dairy'],
    notes: [
      'Sauce-only freezes beautifully; freeze in ice cube trays for one-portion thaws.',
      'The lentils are the secret — they thicken the sauce and add iron without any flavor of their own.',
      'No added salt. Tomatoes and parmesan bring enough.',
    ],
    notes_de: [
      'Die Sauce ohne Nudeln lässt sich super einfrieren — in Eiswürfelformen für Einzelportionen.',
      'Die Linsen sind der Trick — sie verdicken die Sauce und bringen Eisen, ohne eigenen Geschmack.',
      'Kein Salz. Tomaten und Parmesan bringen genug.',
    ],
  },
  {
    id: 'chicken-avocado-toast-12-18',
    bandId: 'one-year',
    dayOfWeek: 'thu',
    title: 'Toddler chicken-avocado toast',
    title_de: 'Kleinkind-Hähnchen-Avocado-Toast',
    whyForThisAge:
      'Self-feeding practice with a soft texture. The avocado replaces the mayo an adult version would use — same creaminess, plus healthy fats that matter for brain development under 2.',
    whyForThisAge_de:
      'Übung im Selber-Essen mit weicher Textur. Die Avocado ersetzt die Mayonnaise einer Erwachsenenversion — gleiche Cremigkeit, plus gesunde Fette, die unter 2 Jahren wichtig fürs Gehirn sind.',
    prepMinutes: 8,
    cookMinutes: 0,
    servings: '1 toddler + 1 adult portion',
    servings_de: '1 Kleinkind-Portion und 1 Erwachsenenportion',
    ingredients: [
      { name: 'cooked chicken breast, shredded', name_de: 'gekochte Hähnchenbrust, zerzupft', metric: '60 g', household: '½ cup', household_de: '½ Tasse' },
      { name: 'ripe avocado, mashed', name_de: 'reife Avocado, zerdrückt', metric: '60 g', household: '½ small', household_de: '½ kleine' },
      { name: 'lemon juice', name_de: 'Zitronensaft', metric: '5 ml', household: '1 tsp', household_de: '1 TL' },
      { name: 'whole-grain bread', name_de: 'Vollkornbrot', metric: '50 g', household: '2 small slices', household_de: '2 kleine Scheiben' },
      { name: 'olive oil or butter', name_de: 'Olivenöl oder Butter', metric: '5 g', household: '1 tsp', household_de: '1 TL' },
    ],
    steps: [
      { text: 'Mash the avocado with the lemon juice in a small bowl.', text_de: 'Avocado mit dem Zitronensaft in einer kleinen Schüssel zerdrücken.' },
      { text: 'Add the shredded chicken and mix gently. The avocado should coat the chicken.', text_de: 'Zerzupftes Hähnchen unterheben. Die Avocado soll das Hähnchen umhüllen.' },
      { text: 'Toast the bread lightly. Spread thinly with butter or olive oil.', text_de: 'Brot leicht toasten. Dünn mit Butter oder Olivenöl bestreichen.' },
      { text: 'Top with the chicken-avocado mix. Cut into thin strips for self-feeding.', text_de: 'Hähnchen-Avocado-Mischung auf das Brot geben. In dünne Streifen schneiden zum Selber-Essen.' },
      { text: 'For an adult portion: same recipe, add salt and pepper after splitting off the toddler share.', text_de: 'Für die Erwachsenenportion: Gleiches Rezept, aber Salz und Pfeffer erst dazugeben, nachdem die Kleinkind-Portion abgetrennt ist.' },
    ],
    allergens: ['wheat'],
    notes: [
      'Use leftover roast chicken if you have it. Rotisserie chicken from the store works (rinse off any seasoning if salt-heavy).',
      'Make the chicken-avocado mix fresh — avocado browns within hours.',
      'Skip the strips and serve as a "deconstructed" plate (chicken + mashed avocado + toast triangles) for a less mess option.',
    ],
    notes_de: [
      'Wenn du Reste vom Sonntagsbraten hast, sind die ideal. Brathähnchen aus dem Supermarkt geht auch (würzige Haut abspülen, wenn sehr salzig).',
      'Die Hähnchen-Avocado-Mischung frisch zubereiten — Avocado wird innerhalb von Stunden braun.',
      'Für weniger Chaos: nicht in Streifen schneiden, sondern „zerlegt" anrichten (Hähnchen + zerdrückte Avocado + Toastdreiecke einzeln).',
    ],
  },
  {
    id: 'sweet-potato-bean-bowl-12-18',
    bandId: 'one-year',
    dayOfWeek: 'fri',
    title: 'Sweet potato + black bean bowl',
    title_de: 'Süsskartoffel-Bohnen-Schüssel',
    whyForThisAge:
      'Iron from the beans, beta-carotene from the sweet potato, fiber from both. Toddlers will mix it together with their hands. That is fine — that is the point.',
    whyForThisAge_de:
      'Eisen aus den Bohnen, Beta-Carotin aus der Süsskartoffel, Ballaststoffe aus beidem. Kleinkinder mischen das mit den Händen. Das ist okay — genau darum geht es.',
    prepMinutes: 5,
    cookMinutes: 35,
    servings: '2 toddler portions',
    servings_de: '2 Kleinkind-Portionen',
    ingredients: [
      { name: 'sweet potato', name_de: 'Süsskartoffel', metric: '200 g', household: '1 medium', household_de: '1 mittlere' },
      { name: 'cooked black beans (drained, rinsed if canned)', name_de: 'gekochte schwarze Bohnen (aus der Dose abgegossen und abgespült)', metric: '60 g', household: '¼ cup', household_de: '4 EL' },
      { name: 'cheddar cheese, grated', name_de: 'Cheddar (oder Gouda), gerieben', metric: '15 g', household: '2 tbsp', household_de: '2 EL' },
      { name: 'olive oil', name_de: 'Olivenöl', metric: '5 ml', household: '1 tsp', household_de: '1 TL' },
      { name: 'cumin (optional)', name_de: 'Kreuzkümmel (optional)', metric: '0.5 g', household: 'small pinch', household_de: 'kleine Prise' },
    ],
    steps: [
      { text: 'Heat oven to 400°F (200°C). Pierce the sweet potato a few times with a fork.', text_de: 'Ofen auf 200 °C vorheizen. Die Süsskartoffel mit der Gabel mehrmals einstechen.' },
      { text: 'Bake 35–45 minutes until completely soft and a fork goes through easily.', text_de: '35–45 Minuten backen, bis sie komplett weich ist und die Gabel mühelos durchgeht.' },
      { text: 'Slit the sweet potato open and scoop the flesh into a bowl. Mash gently with a fork.', text_de: 'Süsskartoffel aufschneiden, das Fruchtfleisch in eine Schüssel löffeln. Mit der Gabel grob zerdrücken.' },
      { text: 'Warm the black beans in a small pan with the olive oil and a pinch of cumin (if using). 2 minutes.', text_de: 'Bohnen in einer kleinen Pfanne mit dem Olivenöl und einer Prise Kreuzkümmel (wenn gewünscht) 2 Minuten erwärmen.' },
      { text: 'Top half of the mashed sweet potato with the beans. Sprinkle the cheese over the warm bean side.', text_de: 'Auf eine Hälfte des Süsskartoffelpürees die Bohnen geben. Käse über die warme Bohnenhälfte streuen.' },
      { text: 'Mix together for the toddler. Cool a bit before serving.', text_de: 'Für das Kleinkind alles verrühren. Kurz abkühlen lassen vor dem Servieren.' },
    ],
    allergens: ['dairy'],
    notes: [
      'Microwave shortcut: pierce, microwave 6–8 minutes on high, rotating once. Same result.',
      'Bake two sweet potatoes at once — second one becomes Saturday lunch.',
      'No salt — beans and cheese bring savory enough.',
    ],
    notes_de: [
      'Mikrowellen-Trick: einstechen, 6–8 Minuten auf höchster Stufe garen, einmal wenden. Gleiches Ergebnis.',
      'Gleich zwei Süsskartoffeln auf einmal backen — die zweite wird Samstagsmittag.',
      'Kein Salz — Bohnen und Käse bringen genug Herzhaftigkeit.',
    ],
  },
  {
    id: 'salmon-pea-rice-12-18',
    bandId: 'one-year',
    dayOfWeek: 'sat',
    title: 'Soft salmon, peas, and rice',
    title_de: 'Weicher Lachs mit Erbsen und Reis',
    whyForThisAge:
      'Fish is on the early-introduction allergen list and salmon is rich in DHA, which matters for brain development under 2. Soft, flaky texture is gentle on developing chewing.',
    whyForThisAge_de:
      'Fisch gehört zu den Allergenen, die früh eingeführt werden sollten. Lachs ist reich an DHA, das unter 2 Jahren wichtig fürs Gehirn ist. Die weiche, blättrige Konsistenz ist gut für die noch entstehende Kaufähigkeit.',
    prepMinutes: 5,
    cookMinutes: 15,
    servings: '2 toddler portions',
    servings_de: '2 Kleinkind-Portionen',
    ingredients: [
      { name: 'salmon fillet, skinless and pin-bones removed', name_de: 'Lachsfilet, ohne Haut und entgrätet', metric: '120 g', household: '4 oz', household_de: '120 g' },
      { name: 'frozen peas', name_de: 'TK-Erbsen', metric: '60 g', household: '½ cup', household_de: '½ Tasse' },
      { name: 'cooked white or brown rice', name_de: 'gekochter weisser oder Vollkornreis', metric: '120 g', household: '¾ cup', household_de: '¾ Tasse' },
      { name: 'butter', name_de: 'Butter', metric: '10 g', household: '2 tsp', household_de: '2 TL' },
      { name: 'lemon zest (optional)', name_de: 'Zitronenschalenabrieb (optional)', metric: '0.5 g', household: 'pinch', household_de: 'Prise' },
    ],
    steps: [
      { text: 'Heat oven to 350°F (175°C). Place salmon on a piece of foil and dot with half the butter.', text_de: 'Ofen auf 175 °C vorheizen. Lachs auf ein Stück Alufolie legen und mit der Hälfte der Butter belegen.' },
      { text: 'Fold foil into a loose packet and bake 12–14 minutes, until the salmon flakes with a fork.', text_de: 'Folie locker zu einem Päckchen falten und 12–14 Minuten backen, bis der Lachs mit der Gabel in Stücke zerfällt.' },
      { text: 'While salmon cooks, simmer peas in a small pan with 2 tbsp water for 3 minutes. Drain.', text_de: 'Während der Lachs gart, Erbsen in einer kleinen Pfanne mit 2 EL Wasser 3 Minuten dünsten. Abgiessen.' },
      { text: 'Flake the salmon carefully with a fork — feel for any bones the fishmonger missed.', text_de: 'Lachs vorsichtig mit der Gabel zerteilen — auf Gräten prüfen, die im Filet vielleicht übersehen wurden.' },
      { text: 'Stir flaked salmon and peas through the warm rice with the remaining butter and the optional lemon zest.', text_de: 'Lachsflocken und Erbsen mit der restlichen Butter und (optional) Zitronenschale unter den warmen Reis heben.' },
      { text: 'Cool to lukewarm before serving.', text_de: 'Auf lauwarm abkühlen lassen vor dem Servieren.' },
    ],
    allergens: ['fish', 'dairy'],
    notes: [
      'Always check carefully for bones, even with "boneless" salmon. A pin-bone is the size of a hair.',
      'Use leftover rice — this is the meal that earns its keep when you batch-cook rice on Sundays.',
      'No salt. The salmon and butter bring enough flavor at this age.',
    ],
    notes_de: [
      'Auch bei „entgrätetem" Lachs immer noch einmal mit den Fingern entlangfahren. Eine Gräte ist nur so dick wie ein Haar.',
      'Reste-Reis ist hier perfekt — das Gericht honoriert das Sonntags-Reis-Vorkochen.',
      'Kein Salz. Lachs und Butter bringen genug Geschmack für dieses Alter.',
    ],
  },
  {
    id: 'yogurt-parfait-12-18',
    bandId: 'one-year',
    dayOfWeek: 'sun',
    title: 'Yogurt parfait — toddler edition',
    title_de: 'Joghurt-Parfait — Kleinkind-Edition',
    whyForThisAge:
      'A no-cook breakfast or snack the toddler can mostly assemble themselves. Whole-milk yogurt continues to be the right choice under age 2 — fat density matters for brain development.',
    whyForThisAge_de:
      'Ein Frühstück oder Snack ohne Kochen, das das Kleinkind grösstenteils selber zusammenstellen kann. Vollmilchjoghurt (3,5 %) bleibt unter 2 Jahren die richtige Wahl — Fett ist wichtig für die Hirnentwicklung.',
    prepMinutes: 5,
    cookMinutes: 0,
    servings: '1 toddler portion',
    servings_de: '1 Kleinkind-Portion',
    ingredients: [
      { name: 'plain whole-milk yogurt', name_de: 'Naturjoghurt, 3,5 % Fett', metric: '120 g', household: '½ cup', household_de: '½ Tasse' },
      { name: 'fresh blueberries, halved', name_de: 'frische Heidelbeeren, halbiert', metric: '40 g', household: '¼ cup', household_de: '¼ Tasse' },
      { name: 'banana, sliced into half-coins', name_de: 'Banane, in halbe Scheiben geschnitten', metric: '60 g', household: '½ medium', household_de: '½ mittlere' },
      { name: 'rolled oats or unsweetened toddler cereal', name_de: 'Haferflocken oder ungesüsstes Kleinkind-Müsli', metric: '15 g', household: '2 tbsp', household_de: '2 EL' },
      { name: 'smooth peanut butter (optional, allergen)', name_de: 'glattes Erdnussmus (optional, Allergen)', metric: '10 g', household: '2 tsp', household_de: '2 TL' },
    ],
    steps: [
      { text: 'In a small bowl, layer half the yogurt, then half the fruit and oats.', text_de: 'In eine kleine Schüssel zuerst die Hälfte des Joghurts, dann die Hälfte des Obstes und der Haferflocken schichten.' },
      { text: 'Repeat with the rest of the yogurt and fruit. Sprinkle remaining oats on top.', text_de: 'Mit dem restlichen Joghurt und Obst wiederholen. Übrige Haferflocken obendrauf streuen.' },
      { text: 'If using peanut butter, thin it with a teaspoon of warm water and drizzle on top — easier to swallow than a glob of pure peanut butter.', text_de: 'Wenn du Erdnussmus dazugibst: mit 1 TL warmem Wasser verdünnen und darüber träufeln — viel leichter zu schlucken als ein dicker Klecks reines Erdnussmus.' },
    ],
    allergens: ['dairy', 'peanut'],
    notes: [
      'No added sugar. The fruit is sweet enough.',
      'Halve the blueberries — whole berries are a choking risk under age 4.',
      'Skip the peanut butter for the first allergen exposure if your family has strong allergy history; talk to your pediatrician first.',
    ],
    notes_de: [
      'Kein zugesetzter Zucker. Das Obst süsst genug.',
      'Heidelbeeren halbieren — ganze Beeren sind unter 4 Jahren Verschluckungsgefahr.',
      'Bei stark belasteter Allergie-Familienanamnese das Erdnussmus für die erste Allergenexposition erst nach Rücksprache mit der Praxis dazugeben.',
    ],
  },

  // ╔══════════════════════════════════════════════════════════════╗
  // ║ Stage: 18–24 months · Toddler eating                        ║
  // ║ Picky-eater proof, family meals, autonomy at the table.     ║
  // ╚══════════════════════════════════════════════════════════════╝

  {
    id: 'hidden-veg-mini-pizza-18-24',
    bandId: 'toddler',
    dayOfWeek: 'mon',
    title: 'Mini pizzas with hidden-veg sauce',
    title_de: 'Mini-Pizzen mit versteckter Gemüse-Sauce',
    whyForThisAge:
      "Pizza is a vehicle. The sauce hides puréed carrot and spinach. Toddlers feel grown-up eating pizza; you smuggle veggies past their suspicion. Both win.",
    whyForThisAge_de:
      'Pizza ist ein Trojanisches Pferd. Die Sauce versteckt pürierte Karotte und Spinat. Das Kleinkind fühlt sich gross beim Pizza-Essen; du schmuggelst Gemüse unbemerkt rein. Win-win.',
    prepMinutes: 10,
    cookMinutes: 12,
    servings: '4 mini pizzas',
    servings_de: '4 Mini-Pizzen',
    ingredients: [
      { name: 'whole-wheat pita bread or English muffins', name_de: 'Vollkorn-Fladenbrot oder kleine Pizza-Böden', metric: '200 g', household: '4 small', household_de: '4 kleine' },
      { name: 'tomato passata', name_de: 'Tomaten-Passata', metric: '120 g', household: '½ cup', household_de: '½ Tasse' },
      { name: 'cooked carrot, finely puréed', name_de: 'gekochte Karotte, fein püriert', metric: '50 g', household: '½ small', household_de: '½ kleine' },
      { name: 'baby spinach, wilted and chopped', name_de: 'Babyspinat, blanchiert und gehackt', metric: '30 g', household: 'small handful', household_de: 'kleine Handvoll' },
      { name: 'mozzarella, grated', name_de: 'Mozzarella, gerieben', metric: '80 g', household: '⅔ cup', household_de: '⅔ Tasse' },
      { name: 'olive oil', name_de: 'Olivenöl', metric: '5 ml', household: '1 tsp', household_de: '1 TL' },
    ],
    steps: [
      { text: 'Heat oven to 200°C / 400°F.', text_de: 'Ofen auf 200 °C vorheizen.' },
      { text: 'Blend tomato passata, carrot purée, and chopped spinach until smooth. Sauce should be unrecognizable as "veggie".', text_de: 'Tomaten-Passata, Karottenpüree und gehackten Spinat glatt pürieren. Die Sauce darf nicht mehr nach „Gemüse" aussehen.' },
      { text: 'Spread sauce on the pita bases. Top with grated mozzarella. Drizzle with olive oil.', text_de: 'Sauce auf die Pizza-Böden streichen. Mit geriebenem Mozzarella belegen. Mit Olivenöl beträufeln.' },
      { text: 'Bake 10–12 minutes until cheese melts and edges crisp slightly.', text_de: '10–12 Minuten backen, bis der Käse geschmolzen ist und der Rand leicht knusprig wird.' },
      { text: 'Cool 3 minutes. Cut into quarters or finger strips.', text_de: '3 Minuten abkühlen lassen. In Viertel oder Fingerstreifen schneiden.' },
    ],
    allergens: ['dairy', 'wheat'],
    notes: [
      'Make a triple batch of the sauce and freeze portions — works for pasta too.',
      'No added salt. Tomato + mozzarella bring plenty.',
    ],
    notes_de: [
      'Dreifache Menge der Sauce machen und Portionen einfrieren — funktioniert auch für Nudeln.',
      'Kein Salz. Tomate und Mozzarella bringen genug.',
    ],
  },

  {
    id: 'pasta-pea-salad-18-24',
    bandId: 'toddler',
    dayOfWeek: 'tue',
    title: 'Toddler pasta salad with peas',
    title_de: 'Kleinkind-Nudelsalat mit Erbsen',
    whyForThisAge:
      "Cold, easy to grab. Peas are the perfect pincer-grasp food for a 2-year-old's perfecting motor skills. A great picnic/take-along option.",
    whyForThisAge_de:
      'Kalt, einfach zu greifen. Erbsen sind das perfekte Pinzettengriff-Lebensmittel für die feinmotorischen Skills eines 2-Jährigen. Super für Picknick oder unterwegs.',
    prepMinutes: 5,
    cookMinutes: 10,
    servings: '4 toddler portions',
    servings_de: '4 Kleinkind-Portionen',
    ingredients: [
      { name: 'small pasta (fusilli or shells)', name_de: 'kleine Nudeln (Fusilli oder Muscheln)', metric: '120 g', household: '4 oz dry', household_de: '120 g roh' },
      { name: 'frozen peas', name_de: 'TK-Erbsen', metric: '100 g', household: '¾ cup', household_de: '¾ Tasse' },
      { name: 'feta cheese, crumbled', name_de: 'Feta, zerbröselt', metric: '50 g', household: '¼ cup', household_de: '¼ Tasse' },
      { name: 'olive oil', name_de: 'Olivenöl', metric: '15 ml', household: '1 tbsp', household_de: '1 EL' },
      { name: 'lemon juice', name_de: 'Zitronensaft', metric: '5 ml', household: '1 tsp', household_de: '1 TL' },
    ],
    steps: [
      { text: 'Cook pasta per package directions. In the last 2 minutes, add peas to the same pot.', text_de: 'Nudeln nach Packungsanweisung kochen. In den letzten 2 Minuten Erbsen mit in den Topf geben.' },
      { text: 'Drain pasta and peas together. Rinse briefly with cool water to stop cooking.', text_de: 'Nudeln und Erbsen zusammen abgiessen. Kurz mit kaltem Wasser abspülen, damit sie nicht weitergaren.' },
      { text: 'Toss with olive oil and lemon juice. Sprinkle feta on top.', text_de: 'Mit Olivenöl und Zitronensaft vermengen. Mit Feta bestreuen.' },
      { text: 'Cool to room temperature before serving. Keeps 2 days in the fridge.', text_de: 'Auf Raumtemperatur abkühlen lassen. Hält 2 Tage im Kühlschrank.' },
    ],
    allergens: ['wheat', 'dairy'],
    notes: [
      'Feta is salty — only a small amount. Skip if your toddler is also getting salty food at other meals today.',
      'Add tinned tuna or cooked chicken to bulk up the protein.',
    ],
    notes_de: [
      'Feta ist salzig — nur eine kleine Menge nehmen. Weglassen, wenn das Kleinkind heute schon andere salzige Mahlzeiten hatte.',
      'Mit Thunfisch aus der Dose oder gekochtem Hähnchen das Eiweiss aufstocken.',
    ],
  },

  {
    id: 'mild-chicken-curry-18-24',
    bandId: 'toddler',
    dayOfWeek: 'wed',
    title: 'Mild chicken-coconut curry',
    title_de: 'Mildes Hähnchen-Kokos-Curry',
    whyForThisAge:
      "Toddlers can handle gentle spice (sweet, not hot). Coconut milk softens any sharpness. This is a doorway to non-Western flavors that future-toddler will appreciate. Iron + healthy fats.",
    whyForThisAge_de:
      'Kleinkinder vertragen milde Gewürze (süsslich, nicht scharf). Kokosmilch puffert alle Schärfe ab. Das ist eine Tür zu nicht-europäischen Aromen, die das spätere Kleinkind schätzen wird. Eisen und gesunde Fette.',
    prepMinutes: 10,
    cookMinutes: 20,
    servings: '4 portions (family-size)',
    servings_de: '4 Portionen (Familiengrösse)',
    ingredients: [
      { name: 'chicken thighs, skinless, cubed', name_de: 'Hähnchenschenkel ohne Haut, gewürfelt', metric: '300 g', household: '10 oz', household_de: '300 g' },
      { name: 'coconut milk (full-fat)', name_de: 'Kokosmilch (Vollfett)', metric: '200 ml', household: '¾ cup', household_de: '200 ml' },
      { name: 'sweet potato, cubed', name_de: 'Süsskartoffel, gewürfelt', metric: '200 g', household: '1 medium', household_de: '1 mittlere' },
      { name: 'mild curry powder', name_de: 'mildes Curry-Pulver', metric: '5 g', household: '1 tsp', household_de: '1 TL' },
      { name: 'turmeric', name_de: 'Kurkuma', metric: '2 g', household: '½ tsp', household_de: '½ TL' },
      { name: 'olive oil', name_de: 'Olivenöl', metric: '15 ml', household: '1 tbsp', household_de: '1 EL' },
      { name: 'cooked rice, to serve', name_de: 'gekochter Reis zum Servieren', metric: '300 g', household: '2 cups', household_de: '2 Tassen' },
    ],
    steps: [
      { text: 'Heat olive oil in a pan. Add chicken cubes and sauté 5 minutes until lightly browned.', text_de: 'Olivenöl in einer Pfanne erhitzen. Hähnchenwürfel dazu und 5 Minuten anbraten, bis sie leicht gebräunt sind.' },
      { text: 'Add curry powder and turmeric. Stir 30 seconds to release aroma.', text_de: 'Curry-Pulver und Kurkuma dazu. 30 Sekunden mitrühren, damit die Aromen freikommen.' },
      { text: 'Add sweet potato cubes and coconut milk. Bring to a simmer.', text_de: 'Süsskartoffelwürfel und Kokosmilch dazugeben. Aufkochen lassen.' },
      { text: 'Cover and cook 15 minutes until sweet potato is soft and chicken is cooked through.', text_de: 'Zugedeckt 15 Minuten köcheln, bis die Süsskartoffel weich und das Hähnchen durch ist.' },
      { text: 'For the toddler portion: shred chicken further with a fork. Serve over a small bed of rice.', text_de: 'Für die Kleinkind-Portion: Hähnchen mit der Gabel weiter zerzupfen. Auf einem kleinen Reisbett servieren.' },
    ],
    allergens: [],
    notes: [
      'No added salt — coconut milk has natural richness. Adult portion can be salted separately.',
      "If your toddler refuses anything 'orange' or 'curry-colored', serve the chicken on white rice and the sweet potato separately. Color reduction sometimes wins.",
    ],
    notes_de: [
      'Kein Salz — Kokosmilch hat natürliche Cremigkeit. Erwachsenenportion kann separat gesalzen werden.',
      'Wenn dein Kleinkind „orange" oder „nach Curry aussehend" ablehnt: Hähnchen auf weissem Reis und die Süsskartoffel separat anrichten. Farb-Trennung gewinnt manchmal.',
    ],
  },

  {
    id: 'lentil-soup-family-18-24',
    bandId: 'toddler',
    dayOfWeek: 'thu',
    title: 'Family lentil soup',
    title_de: 'Linsensuppe für die Familie',
    whyForThisAge:
      "One pot, everyone eats it. Iron, fiber, protein, vegetables in a bowl. The whole family adapts up (chili, salt) after the toddler portion is set aside.",
    whyForThisAge_de:
      'Ein Topf, alle essen mit. Eisen, Ballaststoffe, Eiweiss und Gemüse in einer Schüssel. Die Familie passt die Erwachsenen-Portion nach oben an (Chili, Salz), nachdem die Kleinkind-Portion abgetrennt ist.',
    prepMinutes: 10,
    cookMinutes: 30,
    servings: '6 portions',
    servings_de: '6 Portionen',
    ingredients: [
      { name: 'red lentils, rinsed', name_de: 'rote Linsen, abgespült', metric: '200 g', household: '1 cup', household_de: '1 Tasse' },
      { name: 'carrots, diced', name_de: 'Karotten, gewürfelt', metric: '200 g', household: '2 medium', household_de: '2 mittlere' },
      { name: 'onion, diced', name_de: 'Zwiebel, gewürfelt', metric: '100 g', household: '1 small', household_de: '1 kleine' },
      { name: 'crushed tomatoes', name_de: 'gehackte Tomaten (aus der Dose)', metric: '400 g', household: '1 can', household_de: '1 Dose' },
      { name: 'low-sodium vegetable broth or water', name_de: 'salzarme Gemüsebrühe oder Wasser', metric: '800 ml', household: '3¼ cups', household_de: '800 ml' },
      { name: 'olive oil', name_de: 'Olivenöl', metric: '20 ml', household: '1½ tbsp', household_de: '1½ EL' },
      { name: 'cumin', name_de: 'Kreuzkümmel', metric: '2 g', household: '½ tsp', household_de: '½ TL' },
    ],
    steps: [
      { text: 'Heat olive oil in a large pot. Add onion and carrots, sauté 5 minutes.', text_de: 'Olivenöl in einem grossen Topf erhitzen. Zwiebel und Karotten dazu, 5 Minuten anbraten.' },
      { text: 'Add cumin and stir 30 seconds.', text_de: 'Kreuzkümmel dazu und 30 Sekunden mitrühren.' },
      { text: 'Add lentils, tomatoes, and broth. Bring to a simmer.', text_de: 'Linsen, Tomaten und Brühe dazugeben. Aufkochen lassen.' },
      { text: 'Cover and cook 25–30 minutes until lentils are soft.', text_de: 'Zugedeckt 25–30 Minuten köcheln lassen, bis die Linsen weich sind.' },
      { text: 'For the toddler portion: ladle off 1 cup BEFORE adding any salt or chili to the adult portion. Blend toddler portion smooth if desired.', text_de: 'Für die Kleinkind-Portion: 1 Tasse abschöpfen, BEVOR du Salz oder Chili für die Erwachsenenportion dazugibst. Bei Bedarf glatt pürieren.' },
      { text: 'Salt + chili the rest of the pot for the adults.', text_de: 'Den Rest im Topf mit Salz und Chili für die Erwachsenen würzen.' },
    ],
    allergens: [],
    notes: [
      'Keep your toddler portion in a separate freezer container for 3-day re-use.',
      'Iron + vitamin C in tomatoes makes this a strong iron-absorption meal.',
    ],
    notes_de: [
      'Die Kleinkind-Portion in einer separaten Gefrierdose 3 Tage aufbewahren.',
      'Eisen und Vitamin C in den Tomaten machen das zu einer starken Eisenaufnahme-Mahlzeit.',
    ],
  },

  {
    id: 'broccoli-cheese-bake-18-24',
    bandId: 'toddler',
    dayOfWeek: 'fri',
    title: 'Broccoli-cheese family bake',
    title_de: 'Brokkoli-Käse-Auflauf für die Familie',
    whyForThisAge:
      "A classic 'kid-friendly' broccoli vehicle. Cheese and a creamy sauce make broccoli go down where it might otherwise be rejected. Whole-grain pasta or rice underneath for the base.",
    whyForThisAge_de:
      'Ein klassisches „kinderfreundliches" Brokkoli-Vehikel. Käse und eine cremige Sauce machen Brokkoli gut akzeptierbar, wo er sonst abgelehnt würde. Vollkornnudeln oder Reis als Basis darunter.',
    prepMinutes: 10,
    cookMinutes: 25,
    servings: '4 portions',
    servings_de: '4 Portionen',
    ingredients: [
      { name: 'broccoli florets', name_de: 'Brokkoliröschen', metric: '300 g', household: '1 head', household_de: '1 Kopf' },
      { name: 'short whole-wheat pasta', name_de: 'kurze Vollkornnudeln', metric: '200 g', household: '7 oz dry', household_de: '200 g roh' },
      { name: 'butter', name_de: 'Butter', metric: '20 g', household: '1½ tbsp', household_de: '1½ EL' },
      { name: 'plain flour', name_de: 'Mehl', metric: '20 g', household: '2 tbsp', household_de: '2 EL' },
      { name: 'whole milk', name_de: 'Vollmilch', metric: '300 ml', household: '1¼ cups', household_de: '300 ml' },
      { name: 'cheddar, grated', name_de: 'Cheddar, gerieben', metric: '100 g', household: '1 cup', household_de: '1 Tasse' },
    ],
    steps: [
      { text: 'Cook pasta per package. In the last 4 minutes, add broccoli to the same pot. Drain together.', text_de: 'Nudeln nach Packung kochen. In den letzten 4 Minuten Brokkoli mit in den Topf geben. Zusammen abgiessen.' },
      { text: 'In a separate pan, melt butter, whisk in flour, cook 1 minute.', text_de: 'In einem zweiten Topf Butter schmelzen, Mehl einrühren, 1 Minute kochen.' },
      { text: 'Gradually whisk in milk. Cook until thickened (3 minutes).', text_de: 'Nach und nach Milch einrühren. Kochen bis es eingedickt ist (3 Minuten).' },
      { text: 'Stir in 70 g cheese until melted. Pour over pasta and broccoli, mix.', text_de: '70 g Käse einrühren, bis er geschmolzen ist. Über Nudeln und Brokkoli geben und mischen.' },
      { text: 'Transfer to an oven dish, top with remaining cheese. Bake at 200°C for 10 minutes until golden.', text_de: 'In eine Auflaufform geben, mit dem restlichen Käse bestreuen. 10 Minuten bei 200 °C backen, bis goldbraun.' },
    ],
    allergens: ['dairy', 'wheat'],
    notes: [
      'No salt added. The cheese carries it.',
      "Toddlers who refuse broccoli might eat the cheesy outer pieces only. That's still progress — exposure builds.",
    ],
    notes_de: [
      'Kein zugesetztes Salz. Der Käse trägt das.',
      'Kleinkinder, die Brokkoli ablehnen, essen vielleicht nur die käsigen Aussenstücke. Das ist immer noch Fortschritt — die Exposition wirkt.',
    ],
  },

  {
    id: 'salmon-fishcakes-18-24',
    bandId: 'toddler',
    dayOfWeek: 'sat',
    title: 'Salmon-potato cakes',
    title_de: 'Lachs-Kartoffel-Frikadellen',
    whyForThisAge:
      "Soft, savory, and shaped like a perfect toddler bite. Salmon brings DHA for brain development; the potato makes it a complete bite. Use leftover salmon for a 5-minute job.",
    whyForThisAge_de:
      'Weich, herzhaft und in der perfekten Kleinkind-Bissgrösse. Lachs liefert DHA für die Hirnentwicklung; die Kartoffel macht es zu einem ganzen Bissen. Aus Lachs-Resten in 5 Minuten gemacht.',
    prepMinutes: 10,
    cookMinutes: 15,
    servings: '8 mini cakes',
    servings_de: '8 Mini-Frikadellen',
    ingredients: [
      { name: 'cooked salmon, flaked', name_de: 'gekochter Lachs, zerpflückt', metric: '200 g', household: '7 oz', household_de: '200 g' },
      { name: 'mashed potatoes (leftover)', name_de: 'Kartoffelpüree (vom Vortag)', metric: '250 g', household: '1 cup', household_de: '1 Tasse' },
      { name: 'large egg', name_de: 'Ei (Grösse M)', metric: '50 g', household: '1', household_de: '1' },
      { name: 'chopped parsley or chives', name_de: 'gehackte Petersilie oder Schnittlauch', metric: '5 g', household: '2 tsp', household_de: '2 TL' },
      { name: 'breadcrumbs', name_de: 'Semmelbrösel', metric: '40 g', household: '⅓ cup', household_de: '⅓ Tasse' },
      { name: 'olive oil', name_de: 'Olivenöl', metric: '15 ml', household: '1 tbsp', household_de: '1 EL' },
    ],
    steps: [
      { text: 'Heat oven to 200°C / 400°F. Line a baking tray with parchment.', text_de: 'Ofen auf 200 °C vorheizen. Backblech mit Backpapier auslegen.' },
      { text: 'Carefully feel through the salmon for any remaining bones.', text_de: 'Den Lachs vorsichtig nach restlichen Gräten durchfühlen.' },
      { text: 'In a bowl, combine flaked salmon, mashed potato, egg, and herbs.', text_de: 'In einer Schüssel zerpflückten Lachs, Kartoffelpüree, Ei und Kräuter vermengen.' },
      { text: 'Form 8 small patties (about a tablespoon each). Roll in breadcrumbs.', text_de: '8 kleine Frikadellen formen (je ca. 1 EL). In Semmelbröseln wenden.' },
      { text: 'Place on the tray. Brush tops with olive oil. Bake 12–14 minutes until golden.', text_de: 'Aufs Blech legen. Oben mit Olivenöl bestreichen. 12–14 Minuten backen, bis sie goldbraun sind.' },
    ],
    allergens: ['fish', 'egg', 'wheat'],
    notes: [
      'Freezes 2 months. Reheat from frozen 10 min at 180°C.',
      'No salt — fish carries enough natural sodium.',
    ],
    notes_de: [
      '2 Monate im Gefrierfach. Aus gefroren 10 Min bei 180 °C aufwärmen.',
      'Kein Salz — Fisch bringt von Natur aus genug.',
    ],
  },

  {
    id: 'yogurt-berry-bowl-18-24',
    bandId: 'toddler',
    dayOfWeek: 'sun',
    title: 'Berry crunch yogurt bowl',
    title_de: 'Beeren-Crunch-Joghurt-Schüssel',
    whyForThisAge:
      "A no-cook, high-protein, calcium-rich breakfast or snack. The granola adds crunch and texture variety — important at this age when texture aversion can develop.",
    whyForThisAge_de:
      'Ein Frühstück oder Snack ohne Kochen, mit viel Eiweiss und Kalzium. Das Granola bringt Knusprigkeit und Textur-Abwechslung — wichtig in diesem Alter, wo Textur-Abneigungen entstehen können.',
    prepMinutes: 3,
    cookMinutes: 0,
    servings: '1 toddler portion',
    servings_de: '1 Kleinkind-Portion',
    ingredients: [
      { name: 'whole-milk Greek yogurt', name_de: 'griechischer Joghurt (Vollfett)', metric: '120 g', household: '½ cup', household_de: '½ Tasse' },
      { name: 'mixed berries (halved if blueberry/cherry)', name_de: 'gemischte Beeren (halbiert bei Heidelbeeren/Kirschen)', metric: '60 g', household: '⅓ cup', household_de: '⅓ Tasse' },
      { name: 'low-sugar granola or toasted oats', name_de: 'zuckerarmes Müsli oder geröstete Haferflocken', metric: '15 g', household: '2 tbsp', household_de: '2 EL' },
      { name: 'smooth nut butter (optional)', name_de: 'glattes Nussmus (optional)', metric: '10 g', household: '2 tsp', household_de: '2 TL' },
    ],
    steps: [
      { text: 'Spoon yogurt into a small bowl.', text_de: 'Joghurt in eine kleine Schüssel löffeln.' },
      { text: 'Top with berries. Halve any blueberries — whole berries are a choking risk until 4.', text_de: 'Beeren darüber. Heidelbeeren halbieren — ganze Beeren sind bis 4 Jahre Verschluckungsgefahr.' },
      { text: 'Sprinkle granola on top. Drizzle warmed nut butter if using.', text_de: 'Granola darüberstreuen. Bei Bedarf erwärmtes Nussmus darüber träufeln.' },
    ],
    allergens: ['dairy', 'tree_nut'],
    notes: [
      'Choose a granola with <5g sugar per serving — most are sugar bombs.',
      "Skip the nut butter for the first few exposures if your family has tree-nut allergy history.",
    ],
    notes_de: [
      'Müsli mit weniger als 5 g Zucker pro Portion wählen — die meisten sind Zuckerbomben.',
      'Nussmus bei familiärer Baumnussallergie für die ersten Einführungen weglassen.',
    ],
  },

];

// ────────────────────────────────────────────────────────────────────
// Locale accessor — flat view of a recipe in active locale
// ────────────────────────────────────────────────────────────────────

export interface LocalizedIngredient {
  name: string;
  metric: string;
  household: string;
}

export interface LocalizedStep {
  text: string;
}

export interface LocalizedRecipe {
  id: string;
  bandId: string;
  dayOfWeek: Recipe['dayOfWeek'];
  title: string;
  whyForThisAge: string;
  prepMinutes: number;
  cookMinutes: number;
  servings: string;
  ingredients: LocalizedIngredient[];
  steps: LocalizedStep[];
  allergens: RecipeAllergen[];
  notes: string[];
}

export function localeRecipe(recipe: Recipe): LocalizedRecipe {
  const de = isGerman();
  return {
    id: recipe.id,
    bandId: recipe.bandId,
    dayOfWeek: recipe.dayOfWeek,
    title: de ? recipe.title_de : recipe.title,
    whyForThisAge: de ? recipe.whyForThisAge_de : recipe.whyForThisAge,
    prepMinutes: recipe.prepMinutes,
    cookMinutes: recipe.cookMinutes,
    servings: de ? recipe.servings_de : recipe.servings,
    ingredients: recipe.ingredients.map((i) => ({
      name: de ? i.name_de : i.name,
      metric: i.metric,
      household: de ? i.household_de : i.household,
    })),
    steps: recipe.steps.map((s) => ({ text: de ? s.text_de : s.text })),
    allergens: recipe.allergens,
    notes: de ? recipe.notes_de : recipe.notes,
  };
}

// ────────────────────────────────────────────────────────────────────
// Helpers — band lookup & sorting
// ────────────────────────────────────────────────────────────────────

export function recipesForBand(bandId: string): Recipe[] {
  return RECIPES.filter((r) => r.bandId === bandId);
}

const DAY_ORDER: Recipe['dayOfWeek'][] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export function recipesForBandSorted(bandId: string): Recipe[] {
  return recipesForBand(bandId).sort(
    (a, b) => DAY_ORDER.indexOf(a.dayOfWeek) - DAY_ORDER.indexOf(b.dayOfWeek)
  );
}

export const DAY_LABEL: Record<Recipe['dayOfWeek'], string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};

export const DAY_LABEL_DE: Record<Recipe['dayOfWeek'], string> = {
  mon: 'Montag', tue: 'Dienstag', wed: 'Mittwoch', thu: 'Donnerstag',
  fri: 'Freitag', sat: 'Samstag', sun: 'Sonntag',
};

export function localeDayLabel(day: Recipe['dayOfWeek']): string {
  return (isGerman() ? DAY_LABEL_DE : DAY_LABEL)[day];
}

// ────────────────────────────────────────────────────────────────────
// Daily rotation — recipe of the day
//
// Picks one recipe deterministically based on baby age + baby id +
// date. Same baby gets the same recipe all day. Next day, different
// recipe (cycles through the band's pool).
//
// Returns null for ages where solids are not yet appropriate (<6 mo).
// ────────────────────────────────────────────────────────────────────

function bandForAge(ageWeeks: number): string | null {
  if (ageWeeks < 26) return null; // milk-only stages
  if (ageWeeks < 36) return 'starting-solids';
  if (ageWeeks < 52) return 'finger-foods';
  if (ageWeeks < 78) return 'one-year';
  return 'toddler';
}

export function recipeOfTheDayFor(
  ageWeeks: number,
  babyId: string,
  date: Date = new Date()
): Recipe | null {
  const bandId = bandForAge(ageWeeks);
  if (!bandId) return null;

  const pool = recipesForBand(bandId);
  if (pool.length === 0) return null;

  // Day-of-year hash + baby id, FNV-ish mixing for determinism
  const start = Date.UTC(date.getUTCFullYear(), 0, 0);
  const dayOfYear = Math.floor((date.getTime() - start) / 86400000);

  let hash = (date.getUTCFullYear() * 366 + dayOfYear) >>> 0;
  for (let i = 0; i < babyId.length; i++) {
    hash = (Math.imul(hash, 31) + babyId.charCodeAt(i)) >>> 0;
  }

  return pool[hash % pool.length];
}
